export type User = {
  accessToken?: string;
  status?: UserStatus;
  organizationRoleStatus?: UserOrganizationRoleStatus;
  file?: string;
  email?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role?: UserRoles;
  lastLogin?: string;
  loginMethod?: string;
  organizationId?: string;
  defaultOrganizationId?: string;
  // Sprint 33, story-167/169. Personal opt-out for the daily site report email.
  dailyReportEmailEnabled?: boolean;
  organization?: {
    name: string;
    organizationId: string;
    status?: string;
  };
  /**
   * Every organization this user belongs to, with the role held in each. Sent by
   * the server to SUPER_ADMIN callers only — other roles must not learn about a
   * user's memberships in tenants they don't administer.
   */
  organizationRoles?: {
    organizationId: string;
    role: UserRoles;
    status: UserOrganizationRoleStatus;
    organization: {
      organizationId: string;
      name: string;
    };
  }[];
  rememberMe?: boolean;
  locale?: UserLocale;
  createdAt?: string | Date; // Pattern A: Auto-generated timestamp
  isReadOnly?: boolean; // Derived from organizationRoleStatus === READ_ONLY
};

export interface createOrUpdateUserRequest {
  createUser?: User;
  updateOtherUser?: User;
  updateUserOrganizationRole?: User;
}

export interface updateOwnUserRequest {
  updateOwnUser: User;
}

export interface disableOrEnableUserRequest {
  disableUser?: User;
  enableUser?: User;
}

export interface getSelfRequest {
  getSelf: User;
}

export interface getSelfWithOrganizationRequest {
  getSelfWithOrganization: {
    user: User;
    organization: {
      name: string;
      organizationId: string;
      status?: string;
    };
  };
}

export interface getAllowedRolesRequest {
  getAllowedRoles: UserRoles[];
}

export interface getUsersRequest {
  getUsers: User[];
}

export interface getUserByEmailRequest {
  getUserByEmail: User;
}

export interface getPaginatedUsersRequest {
  getPaginatedUsers: {
    users: User[];
    count: number;
  };
}

export enum UserRoles {
  SUPER_ADMIN = 'SUPER_ADMIN',
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  PROJECT_MANAGER = 'PROJECT_MANAGER',
  SUPERINTENDENT = 'SUPERINTENDENT',
  CREW_LEAD = 'CREW_LEAD',
  EMPLOYEE = 'EMPLOYEE',
  REVIEWER = 'REVIEWER',
  // Sprint 25, story-128. Seatless, read-only, web-first external role for a paying client.
  CLIENT = 'CLIENT',
}

/**
 * Sprint 40, story-216. The roles the server lets call `pendingBudgetRequests`
 * (`@HasPermission([ADMIN, OWNER, PROJECT_MANAGER])`). Clients must not fire that query for any
 * other role: the server refuses it and logs `Role not valid`.
 */
export const PENDING_BUDGET_REQUEST_READ_ROLES: readonly string[] = [
  UserRoles.OWNER,
  UserRoles.ADMIN,
  UserRoles.PROJECT_MANAGER,
];

export const canReadPendingBudgetRequests = (role?: UserRoles | string | null): boolean =>
  !!role && PENDING_BUDGET_REQUEST_READ_ROLES.includes(role);

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum UserOrganizationRoleStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
  READ_ONLY = 'READ_ONLY',
  OVERDUE = 'OVERDUE',
}

export enum UserLocale {
  EN = 'en',
  ES = 'es',
}

export type UserOrganization = {
  organizationId: string;
  role: UserRoles;
  userStatus: 'ACTIVE' | 'DISABLED';
  /**
   * BASE subscription status: TRIAL, TRIAL_EXPIRED, ACTIVE, … Null for organizations with
   * no subscription row. A user can own several organizations, each on its own trial
   * clock, so this list is where they see which ones need attention.
   */
  subscriptionStatus?: string | null;
  /** When this organization's trial ends. Null when it is not on a trial. */
  trialEndsAt?: string | null;
  organization: {
    organizationId: string;
    name: string;
    status: string;
  };
};

export interface getUserOrganizationsRequest {
  getUserOrganizations: UserOrganization[];
}

export interface setDefaultOrganizationRequest {
  setDefaultOrganization: User;
}

export interface updateUserOrganizationStatusRequest {
  updateUserOrganizationStatus: User;
}
